import CloseIcon from "@mui/icons-material/Close";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Paper,
  Typography,
} from "@mui/material";
import { Buffer } from "buffer";
import { saveAs } from "file-saver";
import React, { useRef, useState } from "react";
import { toast } from "react-toastify";
import CustomButton from "../Common/CustomButton";
import CustomToast from "../Common/CustomToast";
import useFetch from "../Common/useFetch";

export const downloadExcelFromBuffer = (buffer, fileName) => {
  const excelData = Buffer.from(buffer, "base64");
  const fileType = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
  const blob = new Blob([excelData], { type: fileType });
  saveAs(blob, fileName);
};

const UploadGuidelinesAndErrors = ({ description, isError }: { description: string[]; isError: boolean }) => {
  return (
    <Paper
      elevation={0}
      sx={{
        backgroundColor: isError ? "#f8d7da" : "#e9f6fe",
        borderRadius: 2,
        p: 2,
        mb: 4,
        color: isError ? "error.dark" : "primary.dark",
      }}
    >
      <Typography fontWeight={600} gutterBottom>
        {isError ? "These are the following errors in the uploaded file" : "Processing Guidelines"}
      </Typography>
      <List disablePadding>
        {description.map((text, index) => (
          <ListItem key={index} disableGutters sx={{ p: 0 }}>
            <ListItemText primary={`• ${text}`} />
          </ListItem>
        ))}
      </List>
    </Paper>
  );
};

interface UploadFileCommonDialogProps {
  open: boolean;
  onClose: () => void;
  useCase: "uploadUnpaidInvoices" | "uploadClosingBalanceSummary";
  getAllData: () => void;
}
const UploadFileCommonDialog: React.FC<UploadFileCommonDialogProps> = ({ open, onClose, useCase, getAllData }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [isDownloadingFile, setIsDownloadingFile] = useState<boolean>(false);
  const [errorMessages, setErrorMessages] = useState<string[]>([]);

  const handleFileUpload = async (files: FileList) => {
    if (files.length > 0) {
      setIsUploading(true);
      const formData = new FormData();
      formData.append("file", files[0]);
      formData.append("anchorId", "2");

      const url = useCase === "uploadUnpaidInvoices" ? "/unpaid-invoices/upload" : "/closing-balance/upload";
      setErrorMessages([]);
      await useFetch(url, "POST", {
        data: formData,
        failureMessage:
          useCase === "uploadUnpaidInvoices"
            ? "Failed to upload unpaid invoices"
            : "Failed to upload closing balance summary",
        thenCallBack: (res) => {
          toast.success(
            <CustomToast
              message={`File uploaded successfully: ${res.data.result.success} out of ${
                res.data.result.success + res.data.result.failed
              } files`}
            />
          );
          if (res.data.result.failed > 0) {
            setErrorMessages(res.data.result.errors.map((error) => `${error.row}: ${error.error}`) || []);
          } else {
            setErrorMessages([]);
            onClose();
          }
          getAllData();
          setIsUploading(false);
        },
        catchCallBack: (error) => {
          // Keep validation errors (e.g. missing columns) visible in the dialog, not just in a toast
          const status = error?.response?.status;
          const message = error?.response?.data?.message;
          if (status && status < 500 && message) {
            setErrorMessages([message]);
          }
          setIsUploading(false);
        },
      });
    }
  };

  const handleDownloadTemplate = () => {
    setIsDownloadingFile(true);
    const url = useCase === "uploadUnpaidInvoices" ? "/unpaid-invoices/template" : "/closing-balance/template";
    useFetch(url, "GET", {
      failureMessage: "Failed to download template",
      thenCallBack: (response) => {
        const fileName =
          useCase === "uploadUnpaidInvoices" ? "unpaid-invoices-template.xlsx" : "closing-balance-template.xlsx";
        const templateBuffer =
          useCase === "uploadUnpaidInvoices"
            ? response.data.result.unpaidInvoicesTemplate
            : response.data.result.cbSummaryTemplate;
        downloadExcelFromBuffer(templateBuffer, fileName);
        setIsDownloadingFile(false);
      },
      catchCallBack: () => {
        setIsDownloadingFile(false);
      },
    });
  };

  const triggerFileSelect = () => {
    if (isUploading) return;
    fileInputRef.current?.click();
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length) {
      handleFileUpload(files);
    }
    // Allow re-selecting the same file after fixing it
    e.target.value = "";
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography variant="h6">
          {useCase === "uploadUnpaidInvoices" ? "Upload Unpaid Invoices" : "Upload Closing Balance Summary"}
        </Typography>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ px: 4, py: 3 }}>
        <UploadGuidelinesAndErrors
          description={
            errorMessages.length > 0
              ? errorMessages
              : useCase === "uploadUnpaidInvoices"
              ? [
                  "Files are processed automatically after upload",
                  "Duplicate GRN entries will be updated with latest data",
                  "Invalid or incomplete records will be flagged",
                  "Processing typically takes 30–60 seconds",
                ]
              : [
                  'Use the template from "Download file format" below - keep the "Summary" sheet and column names unchanged',
                  "Required columns: PAN Number, Vendor Names, Scootsy Balance, Closing Balance (All Entity), Entity Wise Hold, BDPO Hold, Remarks",
                  "A file with a missing column is rejected; existing balances are not changed",
                  "PANs not present in the file are removed from the closing balance",
                ]
          }
          isError={errorMessages.length > 0}
        />
        <Box
          onClick={triggerFileSelect}
          sx={{
            p: 6,
            border: "2px dashed #ccc",
            borderRadius: 2,
            textAlign: "center",
            color: "text.secondary",
            backgroundColor: "#fafafa",
            cursor: "pointer",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CloudUploadIcon sx={{ fontSize: 40, color: "#a94fc4" }} />
          <CustomButton
            sx={{ my: 2 }}
            disabled={isUploading}
            startIcon={isUploading ? <CircularProgress size={20} /> : null}
          >
            {useCase === "uploadUnpaidInvoices" ? "Upload Unpaid Invoices" : "Upload Closing Balance Summary"}
          </CustomButton>
          <Typography variant="body2">XLS/CSV formats supported</Typography>
        </Box>
        <Box sx={{ mt: 2, textAlign: "center" }}>
          <CustomButton
            variant="text"
            sx={{ fontWeight: 600 }}
            onClick={handleDownloadTemplate}
            disabled={isDownloadingFile}
            startIcon={isDownloadingFile ? <CircularProgress size={20} /> : null}
          >
            Download file format
          </CustomButton>
        </Box>
        <input ref={fileInputRef} type="file" hidden accept=".xls,.xlsx,.csv" onChange={handleFileSelect} />
      </DialogContent>
    </Dialog>
  );
};

export default UploadFileCommonDialog;
